import { useAppSelector } from '../../hooks'
import { getEditableInventoryCentres } from '../product/productInventory'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useUpdateVendorInventoryModeMutation } from '../../redux-toolkits/vendors/vendors.slice'
import { Vendor } from '../../redux-toolkits/vendors/vendors.types'
import { Button } from '../button'
import { Checkbox } from '../inputs/Checkbox'
import Dropdown from '../inputs/dropdown'
import { TextInput } from '../inputs/textInput'

export const VendorInventoryMode = ({
  vendor,
  className,
  onSuccess,
}: {
  vendor?: Vendor
  className?: string
  onSuccess: () => void
}) => {
  const { user } = useAppSelector((state) => state.auth)
  const editableCentres = getEditableInventoryCentres(
    vendor?.fulfilmentCentres ?? [],
    user?.fulfillmentCenter,
  )
  const canChangeInventoryMode =
    editableCentres.length > 0 &&
    editableCentres.length === vendor?.fulfilmentCentres?.length
  const currentMode = vendor?.inventoryMode ?? 'GLOBAL'
  const [centreInventoryEnabled, setCentreInventoryEnabled] = useState(
    currentMode === 'FULFILMENT_CENTRE',
  )
  const [initialFulfilmentCentreId, setInitialFulfilmentCentreId] = useState('')
  const [updateInventoryMode, { isLoading }] =
    useUpdateVendorInventoryModeMutation()

  useEffect(() => {
    setCentreInventoryEnabled(currentMode === 'FULFILMENT_CENTRE')
    setInitialFulfilmentCentreId('')
  }, [currentMode])

  const isEnablingCentreInventory =
    centreInventoryEnabled && currentMode !== 'FULFILMENT_CENTRE'
  const hasEnoughCentres = (vendor?.fulfilmentCentres?.length ?? 0) >= 2
  const hasChanges =
    centreInventoryEnabled !== (currentMode === 'FULFILMENT_CENTRE')

  const saveInventoryMode = async () => {
    if (!vendor?._id || !hasChanges || !canChangeInventoryMode) return

    const response = await updateInventoryMode({
      id: vendor._id,
      inventoryMode: centreInventoryEnabled ? 'FULFILMENT_CENTRE' : 'GLOBAL',
      initialFulfilmentCentreId: isEnablingCentreInventory
        ? initialFulfilmentCentreId
        : undefined,
    })

    if ('data' in response && response.data?.success) onSuccess()
  }

  return (
    <div className={classNames('flex flex-col gap-4', className)}>
      <div>
        <p className="font-medium text-black">Inventory tracking</p>
        <p className="text-sm text-sec-black mt-1">
          Track every product and variant separately at each fulfilment centre.
        </p>
      </div>

      <Checkbox
        checkStatus={centreInventoryEnabled}
        disabled={!canChangeInventoryMode}
        text="Use individual stock quantities per fulfilment centre"
        handleChange={(enabled) => {
          setCentreInventoryEnabled(enabled)
          setInitialFulfilmentCentreId('')
        }}
      />

      {!canChangeInventoryMode ? (
        <p className="text-sm text-sec-black">
          Changing inventory tracking requires access to all of this creator’s
          fulfilment centres.
        </p>
      ) : undefined}

      {centreInventoryEnabled && !hasEnoughCentres ? (
        <p className="text-sm text-danger">
          Assign at least two fulfilment centres before enabling this setting.
        </p>
      ) : undefined}

      {isEnablingCentreInventory && hasEnoughCentres ? (
        <Dropdown
          className="w-full max-w-[420px]"
          menuClassName="!max-h-[220px]"
          menuButton={
            <TextInput
              title="Current stock location"
              name="initialFulfilmentCentre"
              placeholder="Select the centre holding existing stock"
              value={
                editableCentres.find(
                  ({ _id }) => _id === initialFulfilmentCentreId,
                )?.name ?? ''
              }
              readOnly
              hasIcon={false}
              isDropdown
            />
          }
          menuItems={editableCentres.map((centre) => ({
            name: centre.name,
            value: centre._id,
            onClick: () => setInitialFulfilmentCentreId(centre._id),
          }))}
        />
      ) : undefined}

      {hasChanges ? (
        <Button
          type="button"
          primary
          label="Save inventory setting"
          className="!w-fit"
          loading={isLoading}
          disabled={
            isLoading ||
            !canChangeInventoryMode ||
            (isEnablingCentreInventory &&
              (!hasEnoughCentres || !initialFulfilmentCentreId))
          }
          onClick={saveInventoryMode}
        />
      ) : undefined}
    </div>
  )
}
